import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { FlexBox } from '../box';
import { IconButton } from '../button';
import { styled } from '../styled';
import { Tabs } from '../tabs';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { ActionButtons, sortPageHeaderActions } from './header';
const SELECTOR_TAB_CONTENT = '[data-role="tab-content"]';
const StyledTabHeader = styled(FlexBox)(({ theme, $hasScrollableTabContent }) => ({
    '& [data-role="tabs"]': {
        overflowY: 'visible'
    },
    [`& ${SELECTOR_TAB_CONTENT}`]: {
        overflowY: 'auto',
        flex: '1 1 0',
        height: '100%',
        width: `calc(100% + ${theme.spacing(4)})`,
        marginLeft: theme.spacing(-4),
        paddingLeft: theme.spacing(4),
        marginRight: theme.spacing(-4),
        paddingRight: $hasScrollableTabContent ? theme.spacing(2) : 0
    }
}));
export const TabHeader = React.forwardRef((props, ref) => {
    const { items: _items, selectedTabIndex: _selectedTabIndex, onTabChange: _onTabChange, className, 'data-role': dataRole = 'page-tab-header', 'data-role-value': dataRoleValue } = props;
    const isControlled = _selectedTabIndex !== undefined;
    const [selectedTabIndex, setSelectedTabIndex] = React.useState(_selectedTabIndex ?? 0);
    React.useEffect(() => {
        if (isControlled && _selectedTabIndex !== selectedTabIndex) {
            setSelectedTabIndex(_selectedTabIndex);
        }
    }, [_selectedTabIndex, isControlled, selectedTabIndex]);
    const innerRef = React.useRef(null);
    const [hasScrollableTabContent, setHasScrollableTabContent] = React.useState(false);
    const onTabChange = (index) => {
        setSelectedTabIndex(index);
        _onTabChange?.(index);
    };
    React.useImperativeHandle(ref, () => innerRef.current);
    React.useEffect(() => {
        const container = innerRef.current;
        const tabIndex = selectedTabIndex ?? 0;
        if (!container) {
            return;
        }
        const tabContent = Array.from(container.querySelectorAll(SELECTOR_TAB_CONTENT))?.[tabIndex];
        if (!tabContent) {
            setHasScrollableTabContent(false);
            return;
        }
        const updateScrollableState = () => {
            setHasScrollableTabContent(tabContent.scrollHeight > tabContent.clientHeight);
        };
        updateScrollableState();
        const observer = new ResizeObserver(updateScrollableState);
        observer.observe(tabContent);
        return () => observer.disconnect();
    }, [_items, selectedTabIndex]);
    const header = _items.at(selectedTabIndex)?.header;
    const title = header?.title;
    const info = header?.info;
    const actions = header?.actions
        ? sortPageHeaderActions(header.actions)
        : undefined;
    const items = _items.map(({ header, ...item }) => item);
    const hasHeaderRow = Boolean(title || info || actions?.length);
    const headerRow = hasHeaderRow ? (_jsxs(FlexBox, { hAlign: "space-between", vAlign: "center", children: [_jsxs(FlexBox, { gap: 0.75, vAlign: "center", children: [title && (_jsx(Typography, { variant: "headingMedium", color: "text.primary", children: title })), info && (_jsx(Tooltip, { title: info, children: _jsx(IconButton, { icon: "IconInfo", size: "small" }) }))] }), actions && _jsx(ActionButtons, { actions: actions })] })) : null;
    return (_jsx(StyledTabHeader, { "$hasScrollableTabContent": hasScrollableTabContent, ref: innerRef, className: className, "data-role": dataRole, "data-role-value": dataRoleValue, stretch: true, minHeight: 0, bgcolor: "background.paper", children: _jsx(Tabs, { contentHeader: headerRow, items: items, selectedTabIndex: selectedTabIndex, onTabChange: onTabChange }) }));
});
TabHeader.displayName = 'TabHeader';
